import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import firebaseApp, { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// =========================================================================
// ImgBB API Key (এখানে আপনার ImgBB API Key বসান)
// =========================================================================
const IMGBB_API_KEY = "af3aa5305ed243a9489b195b87a2afe5"; 

const uploadToImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  try {
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
      method: 'POST',
      body: formData
    });
    const data = await res.json();
    return data.data?.url || null;
  } catch (e) {
    return null;
  }
};

const isAndroidNative = () => Boolean(
  typeof window !== 'undefined' &&
  window.AndroidAudio &&
  typeof window.AndroidAudio.syncUserToken === 'function'
);

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const base64 = String(reader.result || '').split(',')[1];
    if (base64) resolve(base64);
    else reject(new Error('Unable to read the selected image.'));
  };
  reader.onerror = () => reject(new Error('Unable to read the selected image.'));
  reader.readAsDataURL(file);
});

const uploadAndroidProfileImage = async (file) => {
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Choose an image smaller than 5 MB.');
  }
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Choose a JPEG, PNG, or WebP image.');
  }

  const uploadImage = httpsCallable(
    getFunctions(firebaseApp, 'us-central1'),
    'uploadAndroidProfileImage'
  );
  const result = await uploadImage({
    imageBase64: await fileToBase64(file),
    mimeType: file.type
  });
  return result.data.imageUrl;
};

const Profile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [currentUid, setCurrentUid] = useState(null);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [bio, setBio] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) {
      navigate('/login');
      return;
    }
    setCurrentUid(userUid);

    // ফায়ারস্টোর থেকে বর্তমান ইউজারের তথ্য লোড করে ফিল্ডে অটো-ফিলাপ
    getDoc(doc(db, 'users', userUid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setFullName(data.fullName || '');
        setUsername(data.username || data.fullName?.toLowerCase().replace(/\s+/g, '') || 'user');
        setPhoneNumber(data.phoneNumber || '');
        setBio(data.bio || '');
        setPhotoURL(data.photoURL || '');
      }
    });
  }, [navigate]);

  // ImgBB প্রোফাইল পিকচার আপলোড
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const androidNative = isAndroidNative();
    setUploadError('');
    setUploading(true);
    try {
      const uploadedUrl = androidNative
        ? await uploadAndroidProfileImage(file)
        : await uploadToImgBB(file);

      if (uploadedUrl) {
        setPhotoURL(uploadedUrl);
      } else if (androidNative) {
        setUploadError('Profile picture upload failed. Please try again.');
      }
    } catch (error) {
      if (androidNative) {
        setUploadError(error.code === 'functions/unauthenticated'
          ? 'Please sign in again, then retry the upload.'
          : error.message || 'Profile picture upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // প্রোফাইল সেভ করার হ্যান্ডলার
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentUid) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUid), {
        fullName: fullName.trim(),
        bio: bio.trim(),
        photoURL: photoURL
      });
  
    } catch (err) {

    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        
        .profile-card { 
          width: 100%; 
          max-width: 480px; 
          height: 100dvh; 
          background: #ffffff; 
          color: #0f172a; 
          display: flex; 
          flex-direction: column; 
          position: relative; 
          overflow-y: auto; 
          box-shadow: 0 0 40px rgba(0,0,0,0.04);
          padding: 24px 20px;
        }
        @media (min-width: 768px) { 
          .profile-card { height: 92vh; border-radius: 36px; border: 1px solid #f1f5f9; } 
        }

        .back-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #f1f5f9;
          border: none;
          color: #0f172a;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin-bottom: 20px;
        }

        .avatar-box {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto 10px;
        }

        .avatar-img {
          width: 120px;
          height: 120px;
          border-radius: 32px;
          object-fit: cover;
          border: 3px solid #f1f5f9;
        }

        .cam-badge {
          position: absolute;
          bottom: -4px;
          right: -4px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #3b82f6;
          color: white;
          border: 3px solid #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .form-group {
          margin-bottom: 20px;
          text-align: left;
        }

        .form-label {
          display: block;
          font-size: 11px;
          font-weight: 800;
          color: #64748b;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
          text-transform: uppercase;
        }

        .form-input {
          width: 100%;
          padding: 14px 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
        }
        .form-input:focus {
          border-color: #3b82f6;
          background: #ffffff;
        }
        .form-input.disabled {
          background: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
        }

        .save-btn {
          width: 100%;
          padding: 16px;
          background: #007bff;
          color: #ffffff;
          border: none;
          border-radius: 24px;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          margin-top: 10px;
          box-shadow: 0 8px 20px rgba(0, 123, 255, 0.25);
          transition: transform 0.15s ease;
        }
        .save-btn:active { transform: scale(0.98); }
      `}</style>

      {/* ফাইল ইনপুট */}
      <input type="file" ref={fileInputRef} accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />

      <div className="profile-card">
        
        {/* ব্যাক বাটন */}
        <button onClick={() => navigate('/dashboard')} className="back-btn" title="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        </button>

        {/* প্রোফাইল পিকচার (কভার ফটো রিমুভড) */}
        <div className="avatar-box">
          <img 
            src={photoURL || "https://constructiononline.com/profile/"} 
            alt="Profile" 
            className="avatar-img" 
          />
          <button onClick={() => fileInputRef.current?.click()} className="cam-badge" title="Change Photo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          </button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#3b82f6' }}>@{username}</span>
          {uploading && <div style={{ fontSize: '11px', color: '#62b875', fontWeight: '700', marginTop: '4px' }}>{isAndroidNative() ? 'Uploading securely...' : 'Uploading to ImgBB...'}</div>}
          {uploadError && <div role="alert" style={{ fontSize: '11px', color: '#dc2626', fontWeight: '700', marginTop: '4px' }}>{uploadError}</div>}
        </div>

        {/* প্রোফাইল ফর্ম */}
        <form onSubmit={handleSaveProfile}>
          
          {/* FULL NAME (পরিবর্তনযোগ্য) */}
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input 
              type="text" 
              className="form-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* USERNAME (লকড - পরিবর্তন করা যাবে না) */}
          <div className="form-group">
            <label className="form-label">Username (Locked)</label>
            <input 
              type="text" 
              className="form-input disabled"
              value={username}
              readOnly
            />
          </div>

          {/* VERIFIED PHONE NUMBER (লকড - পরিবর্তন করা যাবে না) */}
          <div className="form-group">
            <label className="form-label">Verified Phone Number (Locked)</label>
            <input 
              type="text" 
              className="form-input disabled"
              value={phoneNumber}
              readOnly
            />
          </div>

          {/* BIO (পরিবর্তনযোগ্য) */}
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea 
              className="form-input"
              style={{ height: '90px', resize: 'none' }}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Write something about yourself..."
            />
          </div>

          {/* SAVE BUTTON */}
          <button type="submit" className="save-btn" disabled={saving || uploading}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>

        </form>

      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default Profile;